/**
 * Confirm Loki Issue Client Tool
 * HITL tool with ownership toggle support
 */

import { makeClientTool } from "@zapier/ai-toolkit";
import { z } from "zod";
import { ConfirmLokiIssue } from "./ConfirmLokiIssue";

export const ConfirmLokiIssueSchema = z.object({
  type: z.enum(["bug", "feature"]).describe("Issue type"),
  title: z.string().describe("Issue title"),
  body: z.string().describe("Formatted issue body"),
  affectedApp: z.string().describe("Affected application name"),
  selectedApi: z.string().describe("CLI API identifier"),
  isPartnerOwned: z.boolean().describe("Whether app is partner-owned"),
  affectedUsers: z.array(z.string()).describe("Affected user emails"),

  // Ownership control fields
  ownershipReason: z
    .string()
    .optional()
    .describe("How ownership was determined"),
  canToggleOwnership: z
    .boolean()
    .optional()
    .describe("Whether user can switch formats"),
  displayOwnershipToggle: z
    .boolean()
    .optional()
    .describe("Show ownership toggle UI"),

  // Data for regeneration (can be either bug or feature data)
  bugData: z
    .object({
      ticketId: z.string(),
      affectedApp: z.string(),
      selectedApi: z.string(),
      actualBehavior: z.string(),
      expectedBehavior: z.string(),
      stepsToReproduce: z.array(z.string()),
      frequency: z.string(),
      firstOccurred: z.string().optional(),
      affectedUsers: z.array(z.string()),
      workaround: z.string().optional(),
      theories: z.string().optional(),
    })
    .optional()
    .describe("Original bug data for potential regeneration"),

  featureData: z
    .object({
      ticketId: z.string(),
      affectedApp: z.string(),
      selectedApi: z.string(),
      featureType: z.enum(["action", "trigger", "search", "general"]),
      useCase: z.string(),
      expectedResult: z.string(),
      considerations: z.string().optional(),
      apiDocUrl: z.string().optional(),
      affectedUsers: z.array(z.string()),
    })
    .optional()
    .describe("Original feature data for potential regeneration"),

  // Optional fields for bugs
  frequency: z.string().optional().describe("Bug frequency"),
  firstOccurred: z.string().optional().describe("When bug first occurred"),

  // Optional fields for features
  featureType: z.string().optional().describe("Type of feature"),

  // Validation info
  validationErrors: z
    .array(z.string())
    .optional()
    .describe("Validation errors if any"),
  validationWarnings: z
    .array(z.string())
    .optional()
    .describe("Validation warnings if any"),
});

export type ConfirmLokiIssueParams = z.infer<typeof ConfirmLokiIssueSchema>;

export const confirmLokiIssueTool = makeClientTool({
  name: "confirm_loki_issue",
  description: `Enhanced Loki issue preview with ownership control.

KEY FEATURES:
- Shows formatted issue preview
- Displays current ownership (partner vs Zapier)
- Allows toggling between formats
- Provides "Create Issue" button to proceed

WORKFLOW:
1. Server tool generates issue with ownership detection
2. THIS tool shows preview with ownership indicator
3. User can:
   a. Click "Create Issue" to proceed as-is
   b. Click "Switch to [Format]" to regenerate
   c. Review validation errors/warnings
4. On toggle, sends regeneration request
5. On create, continues with create_loki_issue

OWNERSHIP CONTROL:
- Shows how ownership was determined (auto/manual)
- Provides one-click format switching
- Preserves all data during regeneration

IMPORTANT:
- This is a HITL tool (no execute method)
- User actions trigger continuation
- Supports seamless format switching`,

  parameters: ConfirmLokiIssueSchema,
  component: ConfirmLokiIssue as any,
  // No execute method - this is a HITL tool
});
